import { useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import { DELETE_LOAN_CATEGORY } from "apollo/mutuations";
import { ADMIN_LOAN_CATEGORIES } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import LoanCategoryAddModal from "components/LoanCategory/LoanCategoryAddModal";
import LoanCategoryEditModal from "components/LoanCategory/LoanCategoryEditModal";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { Trash } from "tabler-icons-react";
import { rolePermissions } from "utiles/permissions";

const LoanCategories = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const roles = JSON.parse(localStorage.getItem("roles")) || [];
  const readOnly = rolePermissions.hasMaker(roles) || rolePermissions.hasChecker(roles);

  const { data, loading, refetch } = useQuery(ADMIN_LOAN_CATEGORIES, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      orderBy: [
        {
          column: "SORT_ORDER",
          order: "ASC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Loan Categories",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.loanCategories.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const [delLoanCategory] = useMutation(DELETE_LOAN_CATEGORY, {
    update(cache, { data: { deleteLoanCategory } }) {
      cache.updateQuery(
        {
          query: ADMIN_LOAN_CATEGORIES,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: searchValue,
            orderBy: [
              {
                column: "SORT_ORDER",
                order: "ASC",
              },
            ],
          },
        },
        (data) => {
          if (data.loanCategories.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              loanCategories: {
                data: [
                  ...data.loanCategories.data.filter(
                    (category) => category.id !== deleteLoanCategory.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  const theme = useMantineTheme();

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "name",
      width: "18%",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.name}</span>;
      },
    },
    {
      label: "Description",
      key: "description",
      sortable: false,
      searchable: true,
      width: "22%",
      render: (rowData) => {
        return <span>{rowData.description || "N/A"}</span>;
      },
    },
    {
      label: "Icon",
      key: "icon",
      sortable: false,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        return rowData.icon ? (
          <i className={rowData.icon} style={{ fontSize: "24px" }}></i>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      label: "Provider",
      key: "provider",
      sortable: false,
      searchable: true,
      width: "15%",
      render: (rowData) => {
        return <span>{rowData.provider?.name || "N/A"}</span>;
      },
    },
    {
      label: "Sort Order",
      key: "sort_order",
      sortable: true,
      searchable: false,
      width: "8%",
      render: (rowData) => {
        return <span>{rowData.sort_order || 0}</span>;
      },
    },
    {
      label: "Status",
      key: "status",
      sortable: false,
      searchable: false,
      width: "8%",
      render: (rowData) => {
        return (
          <span>
            {rowData.status === "ACTIVE" ? (
              <Badge variant="light" color="green">
                Active
              </Badge>
            ) : (
              <Badge variant="light" color="red">
                Inactive
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      width: "14%",
      render: (rowData) => {
        if (readOnly) {
          return null;
        }
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="Edit"
              onClick={() => handleEditLoanCategory(rowData)}
            >
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(`${rowData.id}`)}
            >
              <Trash size={15} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const optionsData = readOnly
    ? undefined
    : {
        actionLabel: "Add Loan Category",
        setAddModal: setOpened,
      };

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };

  const handleEditLoanCategory = (category) => {
    setOpenedEdit(true);
    setEditId(category);
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const deleteLoanCategory = () => {
    delLoanCategory({
      variables: { id: deleteID },
      onCompleted(data) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Loan Category Deleted Successfully",
        });
      },
      onError(error) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to Delete Loan Category",
        });
      },
    });
  };

  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      {!readOnly && <Drawer
        opened={opened}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        padding="xl"
        onClose={() => setOpened(false)}
        position="left"
        size="40%"
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
      >
        <LoanCategoryAddModal
          total={total}
          setTotal={setTotal}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpened={setOpened}
          size={size}
          refetch={refetch}
        />
      </Drawer>}

      {!readOnly && <Drawer
        opened={openedEdit}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
        padding="xl"
        onClose={() => setOpenedEdit(false)}
        position="right"
        size="40%"
      >
        <LoanCategoryEditModal
          setOpenedEdit={setOpenedEdit}
          editData={editId}
          data={data ? data.loanCategories.data : []}
        />
      </Drawer>}

      {!readOnly && <Modal
        opened={openedDelete}
        onClose={() => setOpenedDelete(false)}
        title="Warning"
        centered
      >
        <p>Are you sure you want to delete this loan category?</p>
        <Group position="right">
          <Button onClick={() => deleteLoanCategory()} color="red">
            Delete
          </Button>
        </Group>
      </Modal>}

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            clearInput={clearInput}
            paginatorInfo={data?.loanCategories.paginatorInfo}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            optionsData={optionsData}
            loading={loading}
            data={data ? data.loanCategories.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default LoanCategories;
